import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, BookOpen, Users } from 'lucide-react';
import './Team.css';

const Team: React.FC = () => {
  const locations = [
    {
      city: "Boston, USA",
      status: "Current Location",
    },
  ];

  const expertise = [
    {
      area: "Biomedical Imaging",
      description: "Deep expertise in live-cell imaging methods and temporal data analysis",
      years: "Years of research"
    },
    {
      area: "Deep Learning",
      description: "Published imaging algorithms and AI models for biomedical applications",
      years: "Proven track record"
    },
    {
      area: "Agentic Systems",
      description: "Experience building research platforms with multi-agent architectures",
      years: "1+ years"
    }
  ];

  return (
    <section id="team" className="team">
      <div className="container">
        <motion.div 
          className="team-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Our Team & Vision</h2>
          <p className="section-subtitle">
            Domain experts with years of experience in biomedical imaging, 
            deep learning, and cutting-edge AI development.
          </p>
        </motion.div>

        <div className="team-content">
          <motion.div 
            className="locations-section"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="subsection-title">
              <MapPin className="title-icon" />
              Our Location
            </h3>
            <div className="locations-grid">
              {locations.map((location, index) => (
                <div key={index} className="location-card">
                  <h4 className="location-city">{location.city}</h4>
                  <span className="location-status">{location.status}</span>
                </div>
              ))}
            </div>
            <div className="location-rationale">
              <p>
                <strong>Why Boston?</strong> Strategic positioning near leading research institutions 
                and biotech companies. Easier distribution and closer client relationships in the 
                heart of the life sciences ecosystem.
              </p>
            </div>
          </motion.div>

          <motion.div 
            className="expertise-section"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h3 className="subsection-title">
              <BookOpen className="title-icon" />
              Our Expertise
            </h3>
            <div className="expertise-grid">
              {expertise.map((skill, index) => (
                <motion.div 
                  key={index}
                  className="expertise-card"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <h4 className="expertise-area">{skill.area}</h4>
                  <span className="expertise-years">{skill.years}</span>
                  <p className="expertise-description">{skill.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            className="vision-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="subsection-title">
              <Users className="title-icon" />
              Our Mission
            </h3>
            <div className="vision-card">
              <p className="vision-text">
                We're solving the growing pain point in biomedical research: 
                <strong> making complex imaging analysis accessible</strong> to scientists, 
                doctors, and researchers without extensive programming backgrounds. 
                Our multi-agent systems democratize cutting-edge analysis capabilities, 
                accelerating scientific discovery and improving patient outcomes.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Team;